import { useInfiniteQuery } from "@tanstack/react-query";
import { getNextPageParam, getPreviousPageParam } from "../../../utils/api";
import { GET_SECTIONS_QUERY_KEY, getSections } from "../api/rootScreenApi";
import { ContentCardGrid } from "./ContentCardGrid";
import { memo, useEffect } from "react";
import { sizeMap } from "../../../components/cards/hooks/useGridCardDimensions";
import { useEntityCache } from "../../../hooks/useEntityCache";
import {
  Bundle,
  Comic,
  ContentSectionCustomCard,
  GCChannel,
} from "../../../types";
import { useAccountData } from "../../../hooks/useAccountData";
import { useTopBarHeight } from "../../../hooks/userTopBarHeight";
import { useVirtualScrollRestoration } from "../../../hooks/useVirtualScrollRestoration";
import { SomethingWentWrong } from "../../../components/errors/components/SomethingWentWrong";
import { registerAndTrackRecsysRequest } from "../../../analytics/recsys/tracking";
import { clearRecsysStreamSessionId } from "../../../analytics/recsys/streamSession";

export const CMSFeed = memo(
  ({
    activeStreamId,
    onScroll,
    fullSize,
    header,
    activeChannel,
    currentChannelSlug,
    currentSlug,
  }: {
    activeStreamId?: string;
    onScroll?: (scrollableRef: HTMLDivElement) => void;
    fullSize?: boolean;
    header?: React.ReactNode;
    activeChannel?: GCChannel;
    currentChannelSlug?: string;
    currentSlug?: string;
  }) => {
    const accountData = useAccountData();
    const { saveEntityToCache, saveComic } = useEntityCache();
    const topBarHeight = useTopBarHeight();

    // Scroll restoration for virtualized list - different streams = different scroll positions
    const { onVisibleIndexChange, restoreToIndex } =
      useVirtualScrollRestoration({
        keySegment: activeStreamId,
      });

    useEffect(() => {
      return () => {
        clearRecsysStreamSessionId(activeStreamId);
      };
    }, [activeStreamId]);

    const {
      data,
      isLoading,
      isFetching,
      error,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
    } = useInfiniteQuery({
      enabled: !!accountData && !!activeStreamId,
      queryKey: [GET_SECTIONS_QUERY_KEY, accountData?.user?.id, activeStreamId],
      networkMode: "offlineFirst",
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      queryFn: async ({ pageParam, queryKey, signal }) => {
        const activeStreamId = queryKey[2];
        const sections = await getSections(
          activeStreamId as string,
          pageParam,
          signal, // Use React Query's built-in abort signal
        );

        sections.data.payload.results = sections.data.payload.results.filter(
          (section) => {
            const allContent = [
              ...(section.articles ?? []),
              ...(section.artists ?? []),
              ...(section.cards ?? []),
              ...(section.comics ?? []),
              ...(section.releases ?? []),
              ...(section.bundles ?? []),
            ];

            // Check if section has any content
            if (allContent.length === 0) {
              return false;
            }

            allContent.forEach((n) => {
              if (section.meta.layout.style.context === "comics") {
                saveComic(section, n as Comic);
              } else {
                saveEntityToCache(n);
              }

              if (n.entity_type === "ContentSectionCustomCard") {
                const comic = (n as ContentSectionCustomCard).comic;
                if (comic) {
                  saveComic(section, comic);
                }
                const artist = (n as ContentSectionCustomCard).artist;
                if (artist) {
                  saveEntityToCache(artist);
                }

                const release = (n as ContentSectionCustomCard).release;
                if (release) {
                  saveEntityToCache(release);
                }
              }

              if (n.entity_type === "ListBundle") {
                const bundle = n as Bundle;
                saveEntityToCache(bundle);
                // Cache bundle items (releases) as well
                bundle.items?.forEach((item) => {
                  saveEntityToCache({
                    ...item,
                    entity_type: "Release",
                    id: item.release_id,
                  });
                });
              }
            });

            if (!section) return false;
            const { context, mode } = section.meta.layout.style;
            const dimension = sizeMap[context]?.[mode];
            return dimension;
          },
        );

        const streamKey = String(
          currentSlug ??
            currentChannelSlug ??
            activeChannel?.id ??
            activeStreamId ??
            "unknown",
        );
        const page = Number(pageParam ?? 1);

        registerAndTrackRecsysRequest({
          streamId: activeStreamId as string,
          streamKey,
          page,
          sections: sections.data.payload.results,
        });

        return sections;
      },
      initialPageParam: 1,
      getNextPageParam,
      getPreviousPageParam,
    });

    const allRows =
      data?.pages.flatMap((page) => page.data.payload.results) ?? [];

    if (error && error.name !== "CanceledError") {
      return <SomethingWentWrong />;
    }

    return (
      <ContentCardGrid
        header={header}
        key={activeStreamId}
        positionOffset={fullSize ? "0px" : `${topBarHeight.get()}px`}
        data={allRows}
        isLoading={!data || isLoading || (isFetching && !allRows.length)}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        onFetchNextPage={fetchNextPage}
        onScroll={onScroll}
        activeChannel={activeChannel}
        currentChannelSlug={currentChannelSlug}
        currentSlug={currentSlug}
        activeStreamId={activeStreamId}
        onVisibleIndexChange={onVisibleIndexChange}
        restoreToIndex={restoreToIndex}
      />
    );
  },
);
